import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  inject
} from '@angular/core';
import { Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { PrimaryTradeLicenseRequirementForm } from 'src/app/classes/form-groups/primary-trade-license-requirement-form';
import {
  PrimaryTrade,
  PrimaryTradeLicenseRequirement,
  SubNationalDivision,
  TradeCategory
} from 'src/app/classes/models';
import { SubNationalDivisionService } from 'src/app/services/sub-national-division.service';
import { ToastService } from 'src/app/services/toast.service';
import { TradeService } from 'src/app/services/trade.service';
import { UtilityService } from 'src/app/services/utility.service';

@Component({
  selector: 'app-add-edit-trade-requirement-modal',
  templateUrl: './add-edit-trade-requirement-modal.component.html',
  styleUrl: './add-edit-trade-requirement-modal.component.less',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AddEditTradeRequirementModalComponent implements OnInit {
  private _cdr = inject(ChangeDetectorRef);

  constructor(
    private activeModal: NgbActiveModal,
    private UtilityService: UtilityService,
    private ToastService: ToastService,
    private TradeService: TradeService,
    private SubNationalDivisionService: SubNationalDivisionService
  ) {
    // Removing validators here because we don't use this form field in the form for this component.
    const formControlsToRemoveValidation = ['SubNationalDivisionId', 'state'];

    formControlsToRemoveValidation.forEach((controlName) => {
      const control = this.primaryTradeLicenseForm.get(controlName);

      if (control) {
        control.removeValidators(Validators.required);
      }
    });

    this.TradeService.getTrades(
      this.UtilityService.stringifyObjectsInQueryObject({
        attributes: ['id', 'name']
      })
    ).subscribe({
      next: (trades: PrimaryTrade[]) => {
        if (trades.length === 0) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Error retrieving trades.',
            { returnError: true }
          );
        }

        this.trades = trades;
        this._cdr.markForCheck();
      }
    });

    this.SubNationalDivisionService.getSubNationalDivisions().subscribe({
      next: (subNationalDivisions: SubNationalDivision[]) => {
        if (subNationalDivisions.length === 0) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Error retrieving subNationalDivisions.',
            { returnError: true }
          );
        }

        this.subNationalDivisions = subNationalDivisions;
        this._cdr.markForCheck();

        // Might be able to remove this later but for now we want the state to match the
        // SubNationalDivision name.
        this.primaryTradeLicenseForm.controls.SubNationalDivisionId.valueChanges.subscribe(
          {
            next: (id) => {
              const SubNationalDivision = this.subNationalDivisions?.find(
                (snd) => snd.id === id
              );

              if (!SubNationalDivision) {
                throw this.ToastService.addErrorToast(
                  'Error',
                  'Error retrieving subNationalDivision.',
                  { returnError: true }
                );
              }

              const { abbreviation } = SubNationalDivision;

              if (abbreviation) {
                this.primaryTradeLicenseForm.controls.state.patchValue(
                  abbreviation
                );
                this._cdr.markForCheck();
              }
            }
          }
        );
      }
    });
  }

  @Input() PrimaryTradeLicenseRequirement?: PrimaryTradeLicenseRequirement;

  primaryTradeLicenseForm: PrimaryTradeLicenseRequirementForm =
    new PrimaryTradeLicenseRequirementForm();
  trades?: PrimaryTrade[];
  subNationalDivisions?: SubNationalDivision[];
  selectedSubNationalDivisions: SubNationalDivision[] = [];
  selectAllSubNationalDivisions: boolean = false;

  SelectedTrades: PrimaryTrade[] = [];

  handleTradeSelection({ Trades }: { Trades: PrimaryTrade[] }) {
    this.SelectedTrades = Trades;
    this._cdr.markForCheck();
  }

  sortSelectedSubNationalDivisions() {
    this.selectedSubNationalDivisions = this.selectedSubNationalDivisions.sort(
      (a, b) => {
        if (a.name && b.name) {
          return a.name.localeCompare(b.name);
        }
        return 0;
      }
    );
    this._cdr.markForCheck();
  }

  selectAll() {
    if (!this.PrimaryTradeLicenseRequirement?.id) {
      this.selectAllSubNationalDivisions = !this.selectAllSubNationalDivisions;

      if (this.selectAllSubNationalDivisions) {
        this.subNationalDivisions?.forEach((snd) =>
          this.selectedSubNationalDivisions.push(snd)
        );
      } else {
        this.selectedSubNationalDivisions = [];
      }
    }

    this.sortSelectedSubNationalDivisions();
  }

  selectionChange(event: Event, subNationalDivision: SubNationalDivision) {
    event.stopPropagation();

    const index = this.selectedSubNationalDivisions.findIndex(
      (snd) => snd === subNationalDivision
    );

    if (index === -1) {
      this.selectedSubNationalDivisions.push(subNationalDivision);
    } else {
      this.selectedSubNationalDivisions.splice(index, 1);
    }

    this.sortSelectedSubNationalDivisions();
  }

  onBadgeClick(event: Event): void {
    event.stopPropagation();
  }

  close(reason?: string) {
    this.activeModal.dismiss(reason);
  }

  save() {
    const {
      value: { id }
    } = this.primaryTradeLicenseForm;
    const errors = this.UtilityService.generateValidationErrorsObject(
      this.primaryTradeLicenseForm
    );

    if (!id && this.SelectedTrades.length === 0) {
      throw this.ToastService.addErrorToast(
        'Error',
        'A trade must be selected.',
        { returnError: true }
      );
    }

    if (errors) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot save. Form has errors.',
        { returnError: true }
      );
    }

    if (!this.PrimaryTradeLicenseRequirement) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot save. No Trade requirement found.',
        { returnError: true }
      );
    }

    if (this.selectedSubNationalDivisions.length === 0) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot save. No states selected.',
        { returnError: true }
      );
    }

    Object.assign(
      this.PrimaryTradeLicenseRequirement,
      this.primaryTradeLicenseForm.getRawValue()
    );

    this.activeModal.close({
      SelectedStates: this.selectedSubNationalDivisions,
      SelectedTrades: this.SelectedTrades
    });
  }

  selectTrade(Trade: PrimaryTrade, TradeCategory: TradeCategory) {
    this.SelectedTrades.push(Trade);
    this._cdr.markForCheck();
  }

  deselectTrade(Trade: PrimaryTrade, index: number) {
    this.SelectedTrades.splice(index, 1);
    this._cdr.markForCheck();
  }

  ngOnInit(): void {
    if (this.PrimaryTradeLicenseRequirement) {
      const { id, SubNationalDivision, PrimaryTrade } =
        this.PrimaryTradeLicenseRequirement;
      this.primaryTradeLicenseForm.patchValue(
        this.PrimaryTradeLicenseRequirement
      );

      if (id) {
        const controlsToDisable = [
          'PrimaryTradeId',
          'SubNationalDivisionId',
          'city'
        ];

        controlsToDisable.forEach((controlName) => {
          const control = this.primaryTradeLicenseForm.get(controlName);

          if (control) {
            control.disable();
          }
        });

        if (!SubNationalDivision) {
          throw this.ToastService.addErrorToast(
            'Error',
            'No SubNationalDivision found.',
            { returnError: true }
          );
        }

        this.selectionChange(
          new Event('Selection Change'),
          SubNationalDivision
        );

        if (!PrimaryTrade) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing trade information.',
            { returnError: true }
          );
        }

        this.handleTradeSelection({ Trades: [PrimaryTrade] });
      }

      this._cdr.markForCheck();
    }

    const {
      value: { id: PrimaryTradeLicenseRequirementId }
    } = this.primaryTradeLicenseForm;
    if (!PrimaryTradeLicenseRequirementId) {
      this.primaryTradeLicenseForm.controls['PrimaryTradeId'].removeValidators(
        Validators.required
      );
    }
  }
}
